import { useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ClosedCaptionDisabledOutlined from '@mui/icons-material/ClosedCaptionDisabledOutlined';
import ClosedCaptionOutlined from '@mui/icons-material/ClosedCaptionOutlined';
import MicNoneOutlined from '@mui/icons-material/MicNoneOutlined';
import MicOffOutlined from '@mui/icons-material/MicOffOutlined';
import SyncOutlined from '@mui/icons-material/SyncOutlined';
import VideocamOffOutlined from '@mui/icons-material/VideocamOffOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';

import localeMessage from '../localeMessage';
import Countdown from './countdown';
import { sharedDeviceMessage } from '../../vonage/device_settings/messages';

export function BottomBar({
  appointment = {},
  audioOpen,
  connected,
  cameraOpen,
  toggleCamera,
  toggleMic,
  openDialog,
  user = {},
  locale,
  liveCaptions = null,
}) {
  const [sessionOver, setSessionOver] = useState(false);

  const onSessionOver = () => {
    setSessionOver(true);
  };

  const { counsellor } = appointment;
  let partner = null;

  if (appointment.provider_type === 'couples') {
    if (user.id === appointment.couples_appointment_detail.host.id) {
      partner = appointment.couples_appointment_detail.guest;
    } else {
      partner = appointment.couples_appointment_detail.host;
    }
  }

  return (
    <div className="bottom-bar-container">
      <div className="bottom-bar-l">
        {!_.isEmpty(appointment) && (
          <Countdown
            connected={connected}
            appointment={appointment}
            sessionOver={onSessionOver}
          />
        )}
        <div className="bottom-bar-name">
          <p className="name">
            {`${counsellor.first_name} ${counsellor.last_name}`}
          </p>
          <p className="role">{localeMessage('careProvider', locale)}</p>
        </div>
        {partner && (
          <div className="bottom-bar-name">
            <p className="name">
              {`${partner.first_name} ${partner.last_name}`}
            </p>
            <p className="role">{localeMessage('partner', locale)}</p>
          </div>
        )}
      </div>

      <div className="bottom-bar-r">
        <div className="media-controls">
          <button
            aria-label={sharedDeviceMessage('preCall.camera.label', locale)}
            aria-pressed={cameraOpen}
            className={`toggle-camera${cameraOpen ? '' : ' is-off'}`}
            onClick={toggleCamera}
            type="button"
          >
            {cameraOpen ? <VideocamOutlined /> : <VideocamOffOutlined />}
          </button>
          <button
            aria-label={sharedDeviceMessage('preCall.microphone.label', locale)}
            aria-pressed={audioOpen}
            className={`toggle-mic${audioOpen ? '' : ' is-off'}`}
            onClick={toggleMic}
            type="button"
          >
            {audioOpen ? <MicNoneOutlined /> : <MicOffOutlined />}
          </button>
          {appointment.video_provider === 'vonage' &&
            appointment.live_captions_available &&
            liveCaptions?.available && (
              <button
                aria-label={sharedDeviceMessage(
                  liveCaptions.isEnabled
                    ? 'liveCaptions.disable'
                    : 'liveCaptions.enable',
                  locale,
                )}
                aria-pressed={liveCaptions.isEnabled}
                className={`captions-toggle${
                  liveCaptions.isEnabled ? ' is-off' : ''
                }`}
                disabled={liveCaptions.isStarting}
                onClick={() => liveCaptions.onChange(!liveCaptions.isEnabled)}
                title={sharedDeviceMessage(
                  liveCaptions.isEnabled
                    ? 'liveCaptions.disable'
                    : 'liveCaptions.enable',
                  locale,
                )}
                type="button"
              >
                {liveCaptions.isEnabled ? (
                  <ClosedCaptionDisabledOutlined />
                ) : (
                  <ClosedCaptionOutlined />
                )}
              </button>
            )}
        </div>
        <div className="reconnect-controls">
          <button
            aria-label={localeMessage('reconnect', locale)}
            className="reconnect"
            onClick={() => window.location.reload()}
            type="button"
          >
            <SyncOutlined aria-hidden="true" />
            <span>{localeMessage('reconnect', locale)}</span>
          </button>
        </div>
      </div>
      <div className="bottom-bar-complete">
        <button
          className={sessionOver ? 'complete-button-over' : 'complete-button'}
          onClick={openDialog}
          type="button"
        >
          {localeMessage('completeSession', locale)}
        </button>
      </div>
    </div>
  );
}

BottomBar.propTypes = {
  appointment: PropTypes.object,
  audioOpen: PropTypes.bool.isRequired,
  connected: PropTypes.bool.isRequired,
  cameraOpen: PropTypes.bool.isRequired,
  toggleCamera: PropTypes.func.isRequired,
  toggleMic: PropTypes.func.isRequired,
  openDialog: PropTypes.func.isRequired,
  user: PropTypes.object,
  locale: PropTypes.string.isRequired,
  liveCaptions: PropTypes.shape({
    available: PropTypes.bool.isRequired,
    isEnabled: PropTypes.bool.isRequired,
    isStarting: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
  }),
};

function mapStateToProps(state) {
  return {
    locale: (state.user && state.user.locale) || 'en',
  };
}

export default connect(mapStateToProps)(BottomBar);
